import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Gauge,
  Receipt,
  CheckSquare,
  Users,
  Tag,
  SignOut,
  Plus,
  UserCircle,
  MapPin,
  Wallet,
  WifiHigh,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const ROLE_LABEL = {
  hub_user: "Hub User",
  manager: "Manager",
  finance: "Finance",
  admin: "Admin",
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const items = [
    { to: "/dashboard", label: "Overview", icon: Gauge, testId: "nav-overview" },
    { to: "/expenses", label: "Expenses", icon: Receipt, testId: "nav-expenses" },
    { to: "/payments", label: "Payments", icon: Wallet, testId: "nav-payments" },
    { to: "/broadband", label: "Broadband", icon: WifiHigh, testId: "nav-broadband" },
  ];
  if (user && ["manager", "finance", "admin"].includes(user.role)) {
    items.push({ to: "/approvals", label: "Approvals", icon: CheckSquare, testId: "nav-approvals" });
  }
  if (user?.role === "admin") {
    items.push({ to: "/admin/users", label: "Users", icon: Users, testId: "nav-users" });
    items.push({ to: "/admin/categories", label: "Categories", icon: Tag, testId: "nav-categories" });
    items.push({ to: "/admin/hubs", label: "Hubs", icon: MapPin, testId: "nav-hubs" });
  }
  items.push({ to: "/account", label: "My Account", icon: UserCircle, testId: "nav-account" });

  return (
    <div className="min-h-screen flex bg-zinc-50">
      <aside className="sidebar-surface w-64 shrink-0 hidden md:flex flex-col">
        <div className="px-6 py-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-16 rounded-md bg-white grid place-items-center overflow-hidden px-1">
              <img src="/soham-logo.jpg" alt="Soham Express logo" className="h-full w-full object-contain" data-testid="sidebar-logo" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold tracking-tight text-white">Sohom Express</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                Expense Suite
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-zinc-800 text-white"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-zinc-800 grid place-items-center text-sm font-semibold text-zinc-200">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                {ROLE_LABEL[user?.role] || user?.role}
              </div>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              nav("/login");
            }}
            variant="ghost"
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 mt-2"
          >
            <SignOut size={16} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              {ROLE_LABEL[user?.role] || ""} · {user?.hub || "All Hubs"}
            </div>
            <div className="font-heading text-xl font-semibold text-zinc-900">
              Welcome, {user?.name?.split(" ")[0] || "there"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="new-expense-header-btn"
              onClick={() => nav("/expenses/new")}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={16} className="mr-1.5" /> New expense
            </Button>
          </div>
        </header>
        <main className="flex-1 p-6 md:p-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
